import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { TaxAuthority } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Tax Authorities',
    description: 'Manage tax authorities',
    permissionModuleKey: 'tax_authority',
    route: {
      list: '/tax_authority',
      new: '/tax_authority/new',
      detail: (id: number) => `/tax_authority/${id}`,
      edit: (id: number) => `/tax_authority/${id}/edit`,
    },
    api: {
      list: '/p-tax-authority',
      detail: (id: number) => `/p-tax-authority/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search tax authorities...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pt_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pt_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: TaxAuthority) => item.pt_descr,
        options: [],
      },
    ],
    applyFilter: (items: TaxAuthority[], filters: { pt_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pt_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pt_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pt_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Tax Authoritie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/tax_authority/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: TaxAuthority) => {
      window.location.href = `/tax_authority/${item.id}`;
    },
    keyExtractor: (item: TaxAuthority) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new tax authoritie.',
    noSearchResults: 'No tax authorities found matching your search criteria.',
    noFilterResults: 'No tax authorities match the selected filters.',
  },
  delete: {
    title: 'Delete Tax Authoritie',
    message: 'Are you sure you want to delete this tax authoritie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete tax authoritie. Please try again.',
    handler: async (id: number) => {
      const { deleteTaxAuthority } = await import('../../../services/mutations');
      await deleteTaxAuthority(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Tax Authoritie Details',
    notFoundTitle: 'Tax Authoritie not found',
    notFoundMessage: 'The tax authoritie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Tax Authorities',
    defaultTitle: 'Unnamed Tax Authoritie',
    getTitle: (item: TaxAuthority) => {
      return item.pt_descr || `Tax Authoritie #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Tax Authoritie',
      subtitle: 'Add a new tax authoritie',
      submitText: 'Create Tax Authoritie',
    },
    edit: {
      title: 'Edit Tax Authoritie',
      subtitle: 'Update tax authoritie information',
      submitText: 'Update Tax Authoritie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading tax authoritie...',
    errorMessage: 'Failed to save tax authoritie. Please try again.',
    create: async (data: Partial<TaxAuthority>) => {
      const { createTaxAuthority } = await import('../../../services/mutations');
      return await createTaxAuthority(data);
    },
    update: async (id: number, data: Partial<TaxAuthority>) => {
      const { updateTaxAuthority } = await import('../../../services/mutations');
      return await updateTaxAuthority(id, data);
    },
  },
  fields: [
    {
      name: 'pt_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter tax authoritie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Tax Authoritie Information',
        icon: '📝',
        fields: ['pt_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<TaxAuthority>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pt_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: TaxAuthority,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pt_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: TaxAuthority) => {
  return null;
};

export const getInitialFormData = (): Partial<TaxAuthority> => {
  return {
    pt_descr: '',
  };
};

export const mapItemToFormData = (item: TaxAuthority): Partial<TaxAuthority> => {
  return {
    pt_descr: item.pt_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: TaxAuthority,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

